export type MarkdownBlock =
  | { type: 'heading'; level: 1 | 2 | 3; content: string }
  | { type: 'paragraph'; content: string }
  | { type: 'blockquote'; content: string };

export type InlineToken =
  | { type: 'text'; content: string }
  | { type: 'bold'; content: string }
  | { type: 'italic'; content: string }
  | { type: 'underline'; content: string }
  | { type: 'mark'; content: string }
  | { type: 'color'; content: string; color: string }
  | { type: 'image'; alt?: string; src: string; title?: string }
  | { type: 'link'; label: string; href: string; title?: string };

/** A link target: `http(s)://…` or a bare `www.…` address. */
const URL_START = String.raw`(?:https?:\/\/|www\.)`;

/**
 * Authored links, in either bracket order and with the URL on either side:
 * `[Text](https://…)`, `(Text)[https://…]`, `(https://…)[Text]`,
 * `[https://…](Text)`. One side must be a URL, so ordinary brackets such as
 * "[Maria] went to (the beach)" stay plain text.
 */
const LINK_PATTERN_SOURCE = [
  String.raw`\[[^\]\n]+]\(\s*${URL_START}[^)\s]+(?:\s+"[^"]*")?\s*\)`,
  String.raw`\[\s*${URL_START}[^\]\s]+\s*]\([^)\n]+\)`,
  String.raw`\([^)\n]+\)\[\s*${URL_START}[^\]\s]+\s*]`,
  String.raw`\(\s*${URL_START}[^)\s]+\s*\)\[[^\]\n]+]`,
].join('|');

const INLINE_PATTERN = new RegExp(
  [
    String.raw`!\[[^\]]*]\([^)\s]+(?:\s+"[^"]*")?\)`,
    LINK_PATTERN_SOURCE,
    String.raw`\*\*[^*]+\*\*`,
    String.raw`\*[^*]+\*`,
    String.raw`__[^_]+__`,
    String.raw`==[^=]+==`,
    String.raw`%%[^%]+%%`,
  ]
    .map((source) => `(?:${source})`)
    .join('|'),
  'g',
);

const normalizeNewLines = (text: string | null | undefined) =>
  (text ?? '').replace(/\r\n/g, '\n');

export function buildMarkdownBlocks(text: string | null | undefined): MarkdownBlock[] {
  const normalized = normalizeNewLines(text);
  const lines = normalized.split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraphBuffer: string[] = [];
  let blockquoteBuffer: string[] = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) {
      return;
    }
    blocks.push({
      type: 'paragraph',
      content: paragraphBuffer.join('\n').trimEnd(),
    });
    paragraphBuffer = [];
  };

  const flushBlockquote = () => {
    if (!blockquoteBuffer.length) {
      return;
    }
    blocks.push({
      type: 'blockquote',
      content: blockquoteBuffer.join('\n').trimEnd(),
    });
    blockquoteBuffer = [];
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    const blockquoteMatch = line.match(/^>\s?(.*)$/);
    const trimmed = line.trim();

    if (headingMatch) {
      flushParagraph();
      flushBlockquote();
      const level = Math.min(headingMatch[1].length, 3) as 1 | 2 | 3;
      blocks.push({
        type: 'heading',
        level,
        content: headingMatch[2]?.trim() ?? '',
      });
      return;
    }

    if (blockquoteMatch) {
      flushParagraph();
      blockquoteBuffer.push(blockquoteMatch[1] ?? '');
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushBlockquote();
      return;
    }

    if (blockquoteBuffer.length) {
      flushBlockquote();
    }

    paragraphBuffer.push(line);
  });

  flushParagraph();
  flushBlockquote();

  return blocks;
}

export function tokenizeInline(text: string | null | undefined): InlineToken[] {
  const normalized = normalizeNewLines(text);
  if (!normalized) {
    return [];
  }

  const pattern = new RegExp(INLINE_PATTERN);
  const tokens: InlineToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(normalized)) !== null) {
    const { index } = match;
    if (index > lastIndex) {
      tokens.push({ type: 'text', content: normalized.slice(lastIndex, index) });
    }

    const raw = match[0];

    if (raw.startsWith('**') && raw.endsWith('**')) {
      tokens.push({ type: 'bold', content: raw.slice(2, -2) });
    } else if (raw.startsWith('*') && raw.endsWith('*')) {
      tokens.push({ type: 'italic', content: raw.slice(1, -1) });
    } else if (raw.startsWith('__') && raw.endsWith('__')) {
      tokens.push({ type: 'underline', content: raw.slice(2, -2) });
    } else if (raw.startsWith('==') && raw.endsWith('==')) {
      tokens.push({ type: 'mark', content: raw.slice(2, -2) });
    } else if (raw.startsWith('%%') && raw.endsWith('%%')) {
      const inner = raw.slice(2, -2).trim();
      const colorResult = parseColorToken(inner);
      if (colorResult) {
        tokens.push(colorResult);
      } else {
        tokens.push({ type: 'text', content: raw });
      }
    } else if (raw.startsWith('![')) {
      const imageToken = parseImageToken(raw);
      if (imageToken) {
        tokens.push(imageToken);
      } else {
        tokens.push({ type: 'text', content: raw });
      }
    } else if (raw.startsWith('[') || raw.startsWith('(')) {
      const linkToken = parseLinkToken(raw);
      if (linkToken) {
        tokens.push(linkToken);
      } else {
        tokens.push({ type: 'text', content: raw });
      }
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < normalized.length) {
    tokens.push({ type: 'text', content: normalized.slice(lastIndex) });
  }

  return tokens;
}

function parseColorToken(rawContent: string): InlineToken | null {
  if (!rawContent) {
    return null;
  }

  const firstSpaceIndex = rawContent.search(/\s/);
  const color =
    firstSpaceIndex === -1
      ? rawContent.trim()
      : rawContent.slice(0, firstSpaceIndex).trim();
  const content =
    firstSpaceIndex === -1
      ? rawContent.trim()
      : rawContent.slice(firstSpaceIndex + 1).trim();

  if (!color) {
    return null;
  }

  return {
    type: 'color',
    color,
    content: content || color,
  };
}

const LINK_TARGET = new RegExp(`^${URL_START}\\S+$`, 'i');

/** `www.example.com` → `https://www.example.com`; other targets unchanged. */
export function normalizeLinkHref(target: string): string {
  return /^www\./i.test(target) ? `https://${target}` : target;
}

/**
 * An authored link in any of the accepted shapes (see LINK_PATTERN_SOURCE).
 * Whichever side is a URL becomes the target; the other side is the button
 * text. A trailing `"title"` after the URL is kept as the tooltip.
 */
function parseLinkToken(rawContent: string): InlineToken | null {
  const match =
    rawContent.match(/^\[([^\]\n]+)]\(([^)\n]+)\)$/) ??
    rawContent.match(/^\(([^)\n]+)\)\[([^\]\n]+)]$/);

  if (!match) {
    return null;
  }

  const [, first = '', second = ''] = match;
  const parts = [first, second].map((part) => {
    const withTitle = part.trim().match(/^(\S+)\s+"([^"]*)"$/);
    return withTitle
      ? { text: withTitle[1], title: withTitle[2] }
      : { text: part.trim(), title: undefined as string | undefined };
  });

  // Prefer the second part as the URL when both qualify ([url](url)).
  const urlIndex = LINK_TARGET.test(parts[1].text) ? 1 : LINK_TARGET.test(parts[0].text) ? 0 : -1;
  if (urlIndex === -1) {
    return null;
  }

  const target = parts[urlIndex];
  const label = parts[1 - urlIndex].text.trim();
  if (!label) {
    return null;
  }

  return {
    type: 'link',
    label,
    href: normalizeLinkHref(target.text),
    title: target.title?.trim() || undefined,
  };
}

export type TextUrlSegment =
  | { type: 'text'; content: string }
  | { type: 'url'; href: string; display: string };

const BARE_URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"]+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"*]$/;

/**
 * Trims what is almost always sentence punctuation, not part of the address:
 * "go to https://x.com/a." or "(see https://x.com/a)". A closing bracket stays
 * when the URL itself opened one (Wikipedia-style "/Foo_(bar)").
 */
function trimUrlEnd(candidate: string): string {
  let url = candidate;
  for (;;) {
    if (TRAILING_PUNCTUATION.test(url)) {
      url = url.slice(0, -1);
      continue;
    }
    const last = url.slice(-1);
    const pairs: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
    if (pairs[last]) {
      const opens = url.split(pairs[last]).length - 1;
      const closes = url.split(last).length - 1;
      if (closes > opens) {
        url = url.slice(0, -1);
        continue;
      }
    }
    return url;
  }
}

/**
 * Splits plain text into text and URL segments, finding every bare address
 * (`https://…`, `http://…`, `www.…`) so existing lesson content needs no edit
 * to get clickable links.
 */
export function splitTextByUrls(text: string | null | undefined): TextUrlSegment[] {
  const source = text ?? '';
  const segments: TextUrlSegment[] = [];
  let lastIndex = 0;

  for (const match of source.matchAll(BARE_URL_PATTERN)) {
    const start = match.index ?? 0;
    const display = trimUrlEnd(match[0]);
    // "www." alone, or an address that was only punctuation after the prefix
    if (!/^(?:https?:\/\/|www\.)[^.\s]/i.test(display)) {
      continue;
    }
    if (start > lastIndex) {
      segments.push({ type: 'text', content: source.slice(lastIndex, start) });
    }
    segments.push({ type: 'url', href: normalizeLinkHref(display), display });
    lastIndex = start + display.length;
  }

  if (lastIndex < source.length) {
    segments.push({ type: 'text', content: source.slice(lastIndex) });
  }

  return segments;
}

function parseImageToken(rawContent: string): InlineToken | null {
  if (!rawContent) {
    return null;
  }

  const match = rawContent.match(
    /^!\[([^\]]*)]\(([^)\s]+)(?:\s+"([^"]*)")?\)$/,
  );

  if (!match) {
    return null;
  }

  const [, alt = '', src = '', title] = match;

  if (!src.trim()) {
    return null;
  }

  return {
    type: 'image',
    alt: alt?.trim() || undefined,
    src: src.trim(),
    title: title?.trim() || undefined,
  };
}

